
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Table from 'src/components/docs/Table';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to enable and manage Data Transparency Messaging for your application.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Data Transparency Messaging Guide' subtitle='How to enable and manage Data Transparency Messaging for your application' mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`As of October 31, 2024, all new Plaid Inc. customers launching to end users in the US and/or Canada are automatically enrolled for Data Transparency Messaging and must `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`select a use case`}</a>{` to use Link in Production. This does not apply to customers who use only products that don't connect to financial institutions, like Identity Verification, or to customers using only Consumer Report or other Plaid Check products.`}</p>
      <p>{`In addition, the consent restrictions described in this article apply to all Robinhood (`}<inlineCode parentName="p">{`ins_54`}</inlineCode>{`) Items, regardless of whether Data Transparency Messaging is enabled.`}</p>
    </Callout>
    <h4 {...{
      "id": "introduction"
    }}>{`Introduction`}</h4>
    <p>{`Plaid has introduced Data Transparency Messaging (DTM) into the Link flow to help you stay in compliance with the `}<a parentName="p" {...{
        "href": "https://plaid.com/resources/compliance/section-1033/"
      }}>{`1033 rule`}</a>{`. This experience provides end users (consumers) with a greater understanding of the types of data that they share with you and Plaid. When using DTM, a user is informed of the specific data types that you are requesting and the reason that you are requesting them (use cases). If you want access to additional data from a user after their initial Link, or to use the data for additional use cases, they must consent to sharing that data through a separate consent flow.`}</p>
    <p>{`This guide covers the implementation changes required to implement the Data Transparency Messaging feature.`}</p>
    <Image src='/assets/img/docs/dtm.png' alt="Plaid's Data Transparency Messaging: Log in at Gingham Bank, disclose account info, select accounts, with product-specific disclosures." expandable='true' caption='DTM introduces product-specific disclosures above the "Continue" buttons on key screens such as the OAuth handoff pane and "Account Select" pane (pictured). If the user clicks the Learn More link within the disclosure, the "Why is this needed" half-pane appears.' mdxType="Image" />
    <h5 {...{
      "id": "who-is-affected"
    }}>{`Who is affected`}</h5>
    <p>{`Data Transparency Messaging (DTM) will be required for all Plaid customers, unless you meet one of the criteria below:`}</p>
    <ul>
      <li parentName="ul">{`You use only products that don't connect to financial institutions: Identity Verification, Monitor, Protect, or Enrich.`}</li>
      <li parentName="ul">{`You serve end users only in Europe. If you serve end users in Canada but not the United States and would like to opt out of Data Transparency Messaging, contact your account manager.`}</li>
      <li parentName="ul">{`You use only Plaid Check products (i.e. Consumer Report) rather than Plaid Inc. products.`}</li>
    </ul>
    <h5 {...{
      "id": "whats-new"
    }}>{`What's new`}</h5>
    <ul>
      <li parentName="ul">{`The API will only return data for products that users have consented to through Link. In addition to the existing `}<inlineCode parentName="li">{`products`}</inlineCode>{` field, we have added a new configuration field called `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` that can be used to gather consent for additional products.`}</li>
      <li parentName="ul">{`Link can be configured to display DTM. The language will appear as a footer on the OAuth pane (shown to consumers before they are directed to an institution's OAuth flow), or the Account Select pane for sessions that do not go through OAuth.`}</li>
      <li parentName="ul">{`If enabled via the Plaid Dashboard, DTM must be configured to include at least one use case, which explains to users the purpose of the data being requested. You may choose up to 3 use cases for each Link customization that will be shown in Link to your users. You can select these use cases via the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`Dashboard Link Customization page`}</a>{`.`}</li>
      <li parentName="ul">{`Products that require data not consented to by users through Link cannot be accessed without going through `}<a parentName="li" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{`.`}</li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`Data Transparency Messaging is available to all customers who are enabled for US or Canada on an opt-in basis. New customers will be automatically enabled for Data Transparency Messaging, and some existing customers have been automatically enabled for Data Transparency Messaging. For existing customers who have not yet been enabled for Data Transparency Messaging, Plaid will provide more details on the enablement timeline once it has been finalized.`}</p>
    </Callout>
    <h4 {...{
      "id": "migration-overview"
    }}>{`Migration overview`}</h4>
    <ol>
      <li parentName="ol">{`Review your API integration and, if necessary, `}<a parentName="li" {...{
          "href": "#additional-consented-products"
        }}>{`populate the `}<inlineCode parentName="a">{`additional_consented_products`}</inlineCode>{` field`}</a>{` with products you want to gather consent for. Every product that you currently use should be included in either the `}<inlineCode parentName="li">{`products`}</inlineCode>{`, `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{`, `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` or `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` fields. For more information on the differences between these fields, see `}<a parentName="li" {...{
          "href": "/docs/link/initializing-products/#overview"
        }}>{`Initializing products`}</a>{`.`}</li>
      <li parentName="ol">{`Update the use cases for each Link customization via the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`Dashboard`}</a>{`.`}</li>
      <li parentName="ol">{`(Optional) `}<a parentName="li" {...{
          "href": "#updating-link-customizations"
        }}>{`Update Link customizations`}</a>{` to enable DTM via the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`Dashboard`}</a>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "additional-consented-products"
    }}>{`Additional consented products`}</h4>
    <p>{`Previously, as long as you were approved and enabled for a product during your Plaid Dashboard Production Request process, you could request and start using it by calling the corresponding product API route on any Item. Going forward, any Item that goes through Link with DTM enabled restricts your access to new product data unless the Item has the required consent; if you do not have the required consent, you will receive the error `}<a parentName="p" {...{
        "href": "/docs/errors/invalid-input/#additional_consent_required"
      }}><inlineCode parentName="a">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode></a>{`. Additional consent will need to be collected through update mode.`}</p>
    <p>{`If you need to use a product but don't want Plaid to try to extract that data immediately, you can create a Link token with `}<a parentName="p" {...{
        "href": "/docs/api/link/#link-token-create-request-additional-consented-products"
      }}><inlineCode parentName="a">{`additional_consented_products`}</inlineCode></a>{` specified. These products will be shown in Link as part of DTM, but will not be fetched or billed until you request them. Specifying `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` will not have any impact on Items created before DTM is enabled, but it is recommended that you start using this field so you can be ready when DTM is enabled for your Link flows.`}</p>
    <p>{`The products in the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` field may not have any overlap with products listed in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` or `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{` arrays. The `}<inlineCode parentName="p">{`products`}</inlineCode>{` array must contain at least one product. In the DTM language, Link will show data scopes corresponding to the union of `}<inlineCode parentName="p">{`products`}</inlineCode>{`, `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{`, `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{`, and `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{`.`}</p>
    <p>{`Products placed in the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` field do not have any impact on institution filtering or account subtype filtering. For example, if `}<inlineCode parentName="p">{`auth`}</inlineCode>{` is an `}<inlineCode parentName="p">{`additional_consented_product`}</inlineCode>{`, users will be allowed to select any account, not just checking/savings accounts.`}</p>
    <p>{`When your existing Items are migrated to DTM, Items in the US or Canada created without DTM will have the following migration applied: the `}<inlineCode parentName="p">{`billed_products`}</inlineCode>{`, along with any additional products that use the same data scopes, will be automatically added to the `}<inlineCode parentName="p">{`consented_products`}</inlineCode>{` field for those Items. You will be notified when your Items are scheduled to be migrated.`}</p>
    <h4 {...{
      "id": "updating-link-customizations"
    }}>{`Updating Link customizations`}</h4>
    <p>{`To opt into DTM, go to your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/data-transparency-v5"
      }}>{`Link customization`}</a>{` or create a new one (you may duplicate an existing customization by using the `}<a parentName="p" {...{
        "href": "/docs/link/customization/#link-customization-overview"
      }}>{`duplicate feature`}</a>{`), click on the Data Transparency section, where you can customize the use case. You must have at least one selected use case to save the customization. You can preview the primary footer language and the detailed panel. This language will appear on the OAuth pane for sessions that go through OAuth, and on Account Select otherwise.`}</p>
    <p>{`Use cases will only be shown in Link if the end user clicks the "Learn more" link within the DTM disclosure text.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Category`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Use cases`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Payments`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Send and receive money, Pay your bills, Make a purchase online, Facilitate business-to-business payments, Fund your account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Identity verification and fraud`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Verify your identity and prevent fraud, Verify your account, Protect against fraud`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Personal / Business finance management`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Track and manage your finances, Prepare your taxes, Get rewards, Invest your money, Do business accounting and tax preparation, Prepare and categorize invoices, Manage employee expense reporting, Track, manage, and build your credit, Access your paycheck sooner, Pay down debt`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Credit underwriting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get considered for a loan, Verify your income`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`There is no restriction enforced on which use case(s) you select beyond the limit of three use cases per customization. For example, you may still select a payments-related use case even if you are not Production-enabled for any payments-related products and not initializing Link with any payments-related products.`}</p>
    <p>{`In the Production environment, the use cases you select will also be visible to consumers via the Plaid Portal (`}<a parentName="p" {...{
        "href": "https://my.plaid.com/"
      }}>{`my.plaid.com`}</a>{`) and to Plaid financial institution partners.`}</p>
    <p>{`Once you've selected how you want to present DTM to users and selected at least one use case, click Publish. Your changes will go into effect the next time you initialize Link with this customization. Only newly created Items created with the updated customization will have the new API behavior; existing Items will not be affected.`}</p>
    <Callout mdxType="Callout">
      <p>{`If you want to prepare for the DTM automatic enablement date without enabling DTM earlier than necessary, you can add use cases to your `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`Link customization`}</a>{` without enabling DTM.`}</p>
    </Callout>
    <p>{`If you currently use additional products after Link that you do not initialize via the `}<inlineCode parentName="p">{`products`}</inlineCode>{` field, it is recommended that you update your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` code to include `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` before enabling this feature in the Plaid Dashboard to avoid losing access to the additional API endpoints on your new Items.`}</p>
    <h4 {...{
      "id": "authorization-records"
    }}>{`Authorization records`}</h4>
    <p>{`You may need to review authorization records to meet audit requirements or to make decisions about collecting additional consent.`}</p>
    <p>{`Current authorization details such as an Item's consented products and consented use cases are available via the `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` endpoint. These are updated in near real time and may be used to configure update mode to request additional consent from a user.`}</p>
    <p>{`Historical authorization records are available via `}<inlineCode parentName="p">{`/consent/events/get`}</inlineCode>{`. These records will be retained and available for at least 3 years (though the earliest records will begin in November 2024).`}</p>
    <p>{`Authorization details and historical authorization records will only be available for Items that have been enabled for Data Transparency Messaging.`}</p>
    <h4 {...{
      "id": "api-changes"
    }}>{`API changes`}</h4>
    <p>{`For Items that have gone through Link with Data Transparency Messaging enabled, there will be new `}<inlineCode parentName="p">{`consented_products`}</inlineCode>{` and `}<inlineCode parentName="p">{`consented_data_scopes`}</inlineCode>{` fields returned by the `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` endpoint.`}</p>
    <p>{`You will see the error code `}<a parentName="p" {...{
        "href": "/docs/errors/invalid-input/#additional_consent_required"
      }}><inlineCode parentName="a">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode></a>{` if you do not have consent to a product you are trying to request.`}</p>
    <p>{`To resolve `}<inlineCode parentName="p">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode>{` errors, you will need to send the user through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode#requesting-additional-consented-products"
      }}>{`update mode`}</a>{`.`}</p>
    <h4 {...{
      "id": "data-scopes-and-consent"
    }}>{`Data scopes and consent`}</h4>
    <p>{`The products you request for Link Initialization are not mapped 1:1 with the different data scopes displayed. Our goal is to allow consumers to understand what types of data they are permissioning, and product names alone can be confusing to consumers. Because data scopes can be broad, in certain cases, you will get consent for more than just the products you initialize Link with. For example, the Assets product requires consent to both the "Transactions" and the "Account Holder Information" data scopes, so the Transactions and Identity products will both be consented to by default. To understand which products you have consent to access for a given Item, you can use the `}<inlineCode parentName="p">{`consented_products`}</inlineCode>{` field on the `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` or product endpoints as detailed above.`}</p>
    <p>{`Note that the data scopes used by Plaid should not be confused with OAuth data scopes. Some institutions that use OAuth define their own data scopes, and users at OAuth institutions may be required to grant consent to both Plaid's data scopes and institution-specific OAuth data scopes during the Link flow. For more information on OAuth data scopes, see `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#scoped-consent"
      }}>{`Scoped consent`}</a>{` in the OAuth documentation.`}</p>
    <h5 {...{
      "id": "data-scope-descriptions"
    }}>{`Data scope descriptions`}</h5>
    <Table.Container>
      <Table.Row title='Account and balance info'>
    May include account details such as account name, type, description, balances, and masked account number.
      </Table.Row>
      <Table.Row title='Contact info'>
    May include account owner name, email, phone, and address.
      </Table.Row>
      <Table.Row title='Account and routing number'>
    Includes account number and financial institution routing numbers.
      </Table.Row>
      <Table.Row title='Transactions'>
    May include transaction details such as amounts, dates, price, location, spending categories and descriptions, and insights like recurring transactions.
      </Table.Row>
      <Table.Row title='Credit and loans'>
    May include details about your credit and loan accounts, such as balance, payment dates and amounts due, credit limit, repayment status, interest rate, loan terms, and originator info.
      </Table.Row>
      <Table.Row title='Bank Statements'>
    May include account info such as activity and usage, and contact details.
      </Table.Row>
      <Table.Row title='Investments'>
    May include info about investment accounts, such as securities details, quantity, price, and transactions.
      </Table.Row>
      <Table.Row title='Risk info'>
    May use account info such as activity and usage, contact details, transaction-related information, and Plaid connection history to provide an assessment of fraud and/or minimize overdrafts
      </Table.Row>
    </Table.Container>
    <h5 {...{
      "id": "data-scopes-by-product"
    }}>{`Data scopes by product`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Plaid Product`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data scopes required`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Assets`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Auth: Instant Auth or Instant Match`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Auth: Database Auth, Database Insights, or Database Match`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Auth: Same-Day Micro-deposits or Instant Micro-deposits`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Auth: Automated Micro-deposits`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions, Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Balance`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Bank Income`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Consumer Report by Plaid Check`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Statements`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Statements`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Identity (includes Identity Match)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Investments`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Investments`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Liabilities`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Credit and Loans`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Protect: Linked Bank`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Protect: Transactions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Signal Transaction Scores`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Transactions (includes Recurring Transactions)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Transfer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Account and routing number`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Investments Move (early availability)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Account and balance info, Contact info, Account and routing number, Investments`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Account and balance info and Contact info are almost always requested; there are some exceptions for products like Database Match or Database Insights where Plaid does not pull data from the bank, and does not require this information.`}</p>
    <p>{`Plaid will disclose all the underlying data scopes needed to deliver the product. For example, Signal Transaction Scores uses Transactions data as one input to derive the score, so we disclose that the data scope Transactions was accessed, even if you do not request the product Transactions in `}<inlineCode parentName="p">{`products`}</inlineCode>{` or `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{`. In this case, Plaid will record the user's consent to the Transactions data scope. If you request a product requiring the Transactions data scope in the future, you will not need to use update mode. (But if you are adding a new use case as well, you will need to use update mode to request consent for that use case, even if you already have the required product consent.)`}</p>
    <p>{`Some Plaid products do not have any associated bank data scopes because they do not rely on access to bank data. For example, Identity Verification, Document Income, and Payroll Income do not rely on bank data.`}</p>
    <h4 {...{
      "id": "requesting-consent-for-additional-products-or-use-cases-in-update-mode"
    }}>{`Requesting consent for additional products or use cases in update mode`}</h4>
    <p>{`If you would like to start collecting product data for an Item that you did not collect enough consent for during initial creation, you are able to send that Item through update mode to collect new consent. For details, see `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/#requesting-additional-consented-products"
      }}>{`Requesting additional consented products`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/#requesting-additional-use-cases"
      }}>{`Requesting additional use cases`}</a>{` in the update mode documentation.`}</p>
    <h4 {...{
      "id": "automatic-migration-path"
    }}>{`Automatic migration path`}</h4>
    <p>{`If you do not choose to follow any of the steps in this guide, your eligible Link sessions (those including a country code of `}<inlineCode parentName="p">{`US`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`CA`}</inlineCode>{`) will be automatically enabled for Data Transparency Messaging. No date has been finalized for this automatic migration; Plaid will provide details when it has been determined.`}</p>
    <p>{`When your team is automatically enabled for Data Transparency Messaging, the following migrations will occur:`}</p>
    <ul>
      <li parentName="ul">{`Plaid will enable DTM for your Items and Link flows. If you have not customized your use cases as described in `}<a parentName="li" {...{
          "href": "#updating-link-customizations"
        }}>{`Updating Link customizations`}</a>{`, the use cases presented in the Link UI will be automatically populated based on information you have previously provided to us (e.g. in the Production Request Form).`}</li>
      <li parentName="ul">{`If you do not specify `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` in your `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call, Plaid will perform an assessment of your historical product add behavior. If, in the last 6 months before the enforcement date, you had billable activity for a product, and that product is not included in the `}<inlineCode parentName="li">{`products`}</inlineCode>{`, `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{`, or `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{` arrays, Plaid will automatically update your Link session to request consent for that product as though that product had been added to the `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` array. To opt out of this behavior, contact your account manager.`}</li>
    </ul>
    <p>{`DTM is mandatory for all customers who are enabled for Plaid in the US. While Canada-based Link sessions are not subject to 1033 enforcement, they will receive DTM to provide enhanced consumer transparency. If you are enabled for Canada but not for the US and want to opt out of automatic DTM enablement, contact your account manager.`}</p>
    <h4 {...{
      "id": "minimum-client-library-version-for-using-additional-consented-products"
    }}>{`Minimum client library version for using additional consented products`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-python/blob/master/CHANGELOG.md"
        }}>{`Python`}</a>{` - 9.3.0`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-node/blob/master/CHANGELOG.md"
        }}>{`Node`}</a>{` - 10.4.0`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-ruby/blob/master/CHANGELOG.md"
        }}>{`Ruby`}</a>{` - 15.5.0`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-java/blob/master/CHANGELOG.md"
        }}>{`Java`}</a>{` - 11.3.0`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-go/blob/master/CHANGELOG.md"
        }}>{`Go`}</a>{` - 3.4.0`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "introduction",
        level: 4,
        title: "Introduction",
      },
{
        id: "migration-overview",
        level: 4,
        title: "Migration overview",
      },
{
        id: "additional-consented-products",
        level: 4,
        title: "Additional consented products",
      },
{
        id: "updating-link-customizations",
        level: 4,
        title: "Updating Link customizations",
      },
{
        id: "authorization-records",
        level: 4,
        title: "Authorization records",
      },
{
        id: "api-changes",
        level: 4,
        title: "API changes",
      },
{
        id: "data-scopes-and-consent",
        level: 4,
        title: "Data scopes and consent",
      },
{
        id: "requesting-consent-for-additional-products-or-use-cases-in-update-mode",
        level: 4,
        title: "Requesting consent for additional products or use cases in update mode",
      },
{
        id: "automatic-migration-path",
        level: 4,
        title: "Automatic migration path",
      },
{
        id: "minimum-client-library-version-for-using-additional-consented-products",
        level: 4,
        title: "Minimum client library version for using additional consented products",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
